import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {Trade} from '../../../model/clearing/trade';
import {ViewConfig} from '../../../model/api/view-config';
import {OrdersTradesTableRequest} from '../../../model/clearing/orders-trades-table-request';
import {OrdersTradesFilter} from '../../../model/clearing/orders-trades-filter';
import {TableColumnConfig} from "../../../model/api/table-column-config";
import {ClearingSelectionInfo} from "../../../model/clearing/clearing-selection-info";
import {CcVscrollTableComponent} from "../../../crystal-elements/cc-vscroll-table/cc-vscroll-table.component";
import {Observable, Subscription} from "rxjs";
import {ApiValidation} from "../../../model/api/api-validation";
import {takeUntil} from "rxjs/operators";
import {DatePipe, DecimalPipe} from "@angular/common";
import {FieldRecord} from "../../../model/api/field-record";
import {ExchangeDetail} from "../../../model/exchange/exchange-detail";
import {FieldRequest} from "../../../model/api/field-request";
import {CommonFieldService} from "../../../services/common/common-field.service";
import {SelectTableColumnsFormComponent} from "./form/select-table-columns-form/select-table-columns-form.component";
import {CCRoles, CCRolesType} from "../../../model/security/cc-roles-type";
import {WebTradeOrderView} from "../../../model/internal/web-trade-order-view";
import {ClearingSelectionSummary} from "../../../model/clearing/clearing-selection-summary";
import {ClearingInfoLoadRequest} from "../../../model/clearing/clearing-info-load-request";
import {AppConfigService} from "../../../services/app-config.service";
import {TableScroll} from "../../../model/api/table-scroll";
import {ClearingTableConfigService} from "../../../services/clearing/clearing-table-config.service";
import {ClearingCookieService} from "../../../services/clearing/clearing-cookie-service";
import {ClearingSelectionService} from "../../../services/clearing/clearing-selection.service";
import {TableWsService} from "../../../services/websocket/table-ws.service";
import {ClearingTableSyncWsService} from "../../../services/clearing/ws/clearing-table-sync-ws.service";
import {TableWsBaseComponent} from "../../../services/websocket/table-ws-base.component";
import {TableRequest} from "../../../model/api/table-request";
import Side = Trade.Side;

@Component({
  template: 'nop',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
    DatePipe,
    DecimalPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClearingTableBaseWsAbsComponent extends TableWsBaseComponent {

  origin: WebTradeOrderView.ORIGIN;

  @Input()
  scrollHeight: number = 0;

  @Input()
  maximizable: boolean = true;

  @Input()
  viewConfig: ViewConfig;
  clearingStateColorMap: any = {};

  @Input()
  userView: WebTradeOrderView;

  @Input()
  ordersTradesFilter: OrdersTradesFilter = new OrdersTradesFilter();

  @Input()
  maximized: boolean = false;
  @Output()
  maximizedChange = new EventEmitter<boolean>();

  defaultSortField: string = '';
  defaultSortOrder: number = 1;

  recordIds: string[] = [];
  @Input()
  selectedIds: string[] = [];
  @Output()
  selectedIdsChange = new EventEmitter<string[]>();

  @Input()
  active: boolean = false;
  @Output()
  activeChange = new EventEmitter<boolean>();

  currentClearingSelectionInfosByLeg: ClearingSelectionInfo[] = [];

  exchanges: FieldRecord<ExchangeDetail>[] = [];

  @ViewChild('ccTable')
  ccTable: CcVscrollTableComponent;
  @ViewChild('selectTableColumnsDialog')
  selectTableColumnsDialog: SelectTableColumnsFormComponent;

  @Input()
  visibleColumns: string[] = [];

  isSelectionInfoLoading: boolean = false;
  summaryRequest: ClearingInfoLoadRequest;

  protected summaryBuyValue: ClearingSelectionSummary = new ClearingSelectionSummary(Side.BUY);
  protected summarySellValue: ClearingSelectionSummary = new ClearingSelectionSummary(Side.SELL);

  private footerInfoSub?: Subscription;
  private selectionInfoSub?: Subscription;
  private selectionInfoRequestVersion: number = 0;

  constructor(cdref: ChangeDetectorRef,
              messageService: MessageService,
              public appConfigService: AppConfigService,
              public clearingWsService: TableWsService,
              translateService: TranslateService,
              protected commonFieldService: CommonFieldService,
              public tableConfigService: ClearingTableConfigService,
              protected clearingCookieService: ClearingCookieService,
              protected clearingTableSyncWsService: ClearingTableSyncWsService,
              protected clearingSelectionService: ClearingSelectionService) {
    super(cdref,
      messageService,
      translateService,
      appConfigService,
      clearingWsService,
      clearingTableSyncWsService);
  }

  override ngOnDestroy() {
    this.clearSelectionInfoRequests();
    super.ngOnDestroy();
  }

  protected initData() {
    super.initData();
    this.commonFieldService.getExchangeCodes(FieldRequest.DEFAULT()).pipe(takeUntil(this.destroy$)).subscribe(fieldResult => {
      this.exchanges = this.getFields(fieldResult);
    });
  }

  override ngAfterContentInit() {
    queueMicrotask(() => {
      if (this.ccTable?.ccTable?.scroller) {
        this.ccTable.ccTable.scroller.autoSize = false;
        this.ccTable.ccTable.scroller.init();
      }
    });
    super.ngAfterContentInit();
  }

  protected refreshConfig() {
    this.getViewTemplate().pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
      if (apiValidation.success) {
        this.viewConfig = apiValidation.item;
        this.refreshTable();
      } else {
        this.errorMessage(apiValidation.message);
      }
    });
    this.clearingStateColorMap = this.appConfigService.clearingStateColorMap;
  }

  protected refreshTable() {
    if (!this.viewConfig) {
      return;
    }
    this.tableConfig = this.tableConfigService.buildTableConfig(this.viewConfig, this.visibleColumns, this.exchanges, this.clearingStateColorMap);
  }

  protected override loadRecords(request: OrdersTradesTableRequest) {
    if (!this.viewConfig) {
      this.refreshConfig();
      this.newRequest = this.tableConfigService.constructRequest(this.tableConfig, this.tableCurrentRequest, request);
    } else {
      this.newRequest = this.tableConfigService.constructRequest(this.tableConfig, this.tableCurrentRequest, request);
      this.forceReload = true;
    }
  }

  override clearReloadTable(request: TableRequest, ignoreRefresh = false) {
    super.clearReloadTable(request, ignoreRefresh);
    this.refreshSelectionInfos();
    // this.ccTable.scrollToTop();
    this.markForCheck();
  }

  private pendingScrollerRefresh = false;
  private recordIndexByIdCache = new Map<string, number>();

  private assignIfChanged(target: any, key: string, value: any): boolean {
    if (target[key] === value) {
      return false;
    }
    target[key] = value;
    return true;
  }

  private lastRecordIdsRef?: string[];

  private ensureRecordIndexCache(): void {
    if (this.lastRecordIdsRef === this.recordIds) {
      return;
    }

    this.recordIndexByIdCache.clear();
    if (this.recordIds) {
      for (let i = 0; i < this.recordIds.length; i++) {
        this.recordIndexByIdCache.set(this.recordIds[i], i);
      }
    }

    this.lastRecordIdsRef = this.recordIds;
  }

  private scheduleScrollerRefresh(): void {
    if (this.pendingScrollerRefresh) {
      return;
    }

    this.pendingScrollerRefresh = true;

    requestAnimationFrame(() => {
      this.pendingScrollerRefresh = false;

      const scroller = this.ccTable?.ccTable?.scroller;
      if (!scroller) {
        return;
      }

      scroller.setSize();
      scroller.calculateOptions();
      scroller.setSpacerSize();
    });
  }

  override applyRecordResult(tableRecordResult, from: number, to: number) {
    const tmpRecords = tableRecordResult.records ? [...tableRecordResult.records] : [];
    const preparedRecords = this.tableConfigService.preloadDisplay(tmpRecords, this.tableConfig, this.selectedIds);

    const selectedIdSet = new Set(this.selectedIds);
    this.ensureRecordIndexCache();

    let anyRowChanged = false;

    for (let i = from; i < to; i++) {
      const newRecord = preparedRecords[i - this.tableCurrentRequest.first];
      if (!newRecord) {
        continue;
      }

      const recordIndex = this.recordIndexByIdCache.get(newRecord['id']) ?? i;
      // let recordIndex = this.getRecordIndexFromId(newRecord);

      let existingRecord = this.records[recordIndex];
      if (!existingRecord) {
        existingRecord = {};
        this.records[recordIndex] = existingRecord;
        anyRowChanged = true;
      }

      let changed = false;

      changed = this.assignIfChanged(existingRecord, 'id', newRecord['id']) || changed;
      changed = this.assignIfChanged(existingRecord, 'selected', selectedIdSet.has(newRecord['id'])) || changed;

      for (const column of this.tableConfig) {
        const field = column.field;

        changed = this.assignIfChanged(existingRecord, field + '_value', newRecord[field + '_value']) || changed;
        changed = this.assignIfChanged(existingRecord, field + '_style', newRecord[field + '_style']) || changed;
        changed = this.assignIfChanged(existingRecord, field + '_class', newRecord[field + '_class']) || changed;
        changed = this.assignIfChanged(existingRecord, field + '_icon', newRecord[field + '_icon']) || changed;
        changed = this.assignIfChanged(existingRecord, field, newRecord[field]) || changed;
      }

      anyRowChanged = anyRowChanged || changed;
    }

    if (anyRowChanged) {
      this.scheduleScrollerRefresh();
    }
  }

  override prepareRequestBeforePost(requestToPrepare: TableRequest): TableRequest {
    const request: any = super.prepareRequestBeforePost(requestToPrepare);
    const newFiltersGrouped = [];
    newFiltersGrouped.push(...WebTradeOrderView.toFilters(this.origin, this.userView));
    if (this.ordersTradesFilter) {
      request.stateFilter = this.ordersTradesFilter.stateFilter;
      if (this.ordersTradesFilter.tradeFilters && this.ordersTradesFilter.tradeFilters.length > 0) {
        newFiltersGrouped.push(...this.ordersTradesFilter.tradeFilters);
      }
    }

    request.filters2 = newFiltersGrouped;
    return request;
  }

  public refreshSelectionInfos() {
    this.reloadClearingSectionInfos();
  }

  protected reloadClearingSectionInfos() {
    const requestVersion = ++this.selectionInfoRequestVersion;
    const selectionIds = [...this.selectedIds];

    this.clearSelectionInfoRequests();
    this.currentClearingSelectionInfosByLeg.splice(0);

    setTimeout(() => {
      if (!this.isCurrentSelectionInfoRequest(requestVersion)) {
        return;
      }
      this.refreshContextMenu();
    });


    this.storeFooterInfoSubscription(
      this.clearingSelectionService.loadFooterInfos(
        selectionIds,
        this.summaryRequest,
        this.getSelectionSummary.bind(this),
        (buy, sell) => {
          if (!this.isCurrentSelectionInfoRequest(requestVersion)) {
            return;
          }
          this.summaryBuyValue = buy;
          this.summarySellValue = sell;
        },
        () => {
          if (!this.isCurrentSelectionInfoRequest(requestVersion)) {
            return;
          }
          this.markForCheck();
        },
        (isLoading) => {
          if (!this.isCurrentSelectionInfoRequest(requestVersion)) {
            return;
          }
          this.isSelectionInfoLoading = isLoading;
        }
      )
    );

    setTimeout(() => {
      if (!this.isCurrentSelectionInfoRequest(requestVersion)) {
        return;
      }

      this.storeSelectionInfoSubscription(
        this.clearingSelectionService.loadSelectionInfos(
          selectionIds,
          this.getSelectionInfoLegs.bind(this),
          (infos) => {
            if (!this.isCurrentSelectionInfoRequest(requestVersion)) {
              return;
            }
            this.currentClearingSelectionInfosByLeg.splice(0);
            this.currentClearingSelectionInfosByLeg.push(...infos);
          },
          () => {
            if (!this.isCurrentSelectionInfoRequest(requestVersion)) {
              return;
            }
            setTimeout(() => {
              if (!this.isCurrentSelectionInfoRequest(requestVersion)) {
                return;
              }
              this.refreshContextMenu();
            });
            this.markForCheck();
          }
        )
      );
    });
  }

  private clearSelectionInfoRequests(): void {
    this.footerInfoSub?.unsubscribe();
    this.footerInfoSub = undefined;
    this.selectionInfoSub?.unsubscribe();
    this.selectionInfoSub = undefined;
  }

  private isCurrentSelectionInfoRequest(requestVersion: number): boolean {
    return requestVersion === this.selectionInfoRequestVersion;
  }

  private storeFooterInfoSubscription(subscription?: Subscription | void): void {
    if (subscription) {
      this.footerInfoSub = subscription;
    }
  }

  private storeSelectionInfoSubscription(subscription?: Subscription | void): void {
    if (subscription) {
      this.selectionInfoSub = subscription;
    }
  }

  // public checkSyncIdsRange(from: number, to: number) {
  //   if (!this.recordIds || !this.records) {
  //     return;
  //   }
  //
  //   const start = Math.max(0, from);
  //   const end = Math.min(this.recordIds.length, to);
  //
  //   for (let i = start; i < end; i++) {
  //     const recordId = this.recordIds[i];
  //     const record = this.records[i];
  //     if (record && record.id !== recordId) {
  //       this.records[i] = undefined;
  //     }
  //   }
  // }

  public checkSyncIds() {
    if (this.recordIds) {
      this.recordIds.forEach((recordId, index) => {
        const record = this.records[index];
        if (record && record.id != recordId) {
          this.records[index] = undefined;
        }
      });
    }
  }

  colOrderChanged(event: TableColumnConfig[]) {
    if (event)
      this.clearingCookieService.storeVisibleColumns(this.userView.id, this.origin, event.filter(value => !value.hidden).map(value => value.field));
  }

  protected getRecordIndexFromId(newRecord) {
    return this.recordIds.findIndex(value => value == newRecord['id']);
  }

  protected getSelectionSummary(req: ClearingInfoLoadRequest): Observable<ApiValidation<ClearingSelectionSummary[]>> {
    return undefined;
  }

  protected getSelectionInfoLegs(req: ClearingInfoLoadRequest): Observable<ApiValidation<ClearingSelectionInfo[]>> {
    return undefined;
  }

  protected getViewTemplate(): Observable<ApiValidation<ViewConfig>> {
    return undefined;
  }

  protected refreshContextMenu() {
  }

  isReadOnly(): boolean {
    return !this.appConfigService.clearingAllocationEnabled ||
      (this.userRoles?.some(role => CCRoles.ROLES_CLEARING_READONLY.includes(role)) &&
        !this.userRoles.includes(CCRolesType.Roles.ROLE_SYSTEM));
  }

  public storeFilterSortStateToCookies(request: TableRequest) {
    if (this.userView == null)
      return;
    this.clearingCookieService.storeFilterSortState(this.userView.id, this.origin, request);
  }


  public onSyncApplied() {
  }

  protected filterSortChanged() {
    // this.ccTable.scrollToTop();
    this.refreshSelectionInfos();
    this.setLoading();
  }

  public override isWsRefreshEnabled(): boolean {
    return !!this.active;
  }
}
